/**
 * BhanzuBot's pure helpers: what it suggests on each page, what its
 * "Looked up" line says, the page it is asked from, times in the history
 * list, which failed answers offer Retry, and what the browser remembers.
 */

/** The longest question the service accepts (400 invalid-request past it). */
export const MAX_MESSAGE_CHARS = 2000
/** From this many characters the composer shows how many are left. */
export const COUNTER_FROM = 1800

export const BOT_CONVERSATION_KEY = 'comms_bot_conversation'
export const BOT_OPEN_KEY = 'comms_bot_open'

// Three questions to start with, by the page the panel is opened on.
const STARTERS = [
    [
        ['/pipelines', '/pipeline/'],
        [
            'How do I create a pipeline for an event?',
            'Why did my pipeline not send anything?',
            'How do I find the pipelines of an event type?',
        ],
    ],
    [
        ['/bulk-send'],
        [
            'What CSV format does Bulk Send need?',
            'How do I schedule a bulk send for later?',
            'What does the pre-send report check?',
        ],
    ],
    [
        [
            '/templates',
            '/create-template',
            '/edit-template',
            '/upload-template',
            '/test-template',
            '/approve-template',
        ],
        [
            'How do I create a WhatsApp template?',
            'How do variables work in a template?',
            'Why is my template waiting for approval?',
        ],
    ],
    [
        ['/reports'],
        [
            'How do I find what was sent to a parent?',
            'How do I download a large report?',
            'What do the delivery statuses mean?',
        ],
    ],
    [
        ['/holidays'],
        [
            'How do I create a holiday?',
            'How are holiday comms sent?',
            'How do I upload holidays in bulk?',
        ],
    ],
    [
        ['/ai'],
        [
            'Which AI tool writes a template for me?',
            'How do I check a CSV before sending it?',
            'How does Comms Finder work?',
        ],
    ],
    [
        ['/settings'],
        [
            'How do I switch the AI model?',
            'How do I add a sender email?',
            'What is sending-domain warmup?',
        ],
    ],
]
const DEFAULT_STARTERS = [
    'What can I do in Comms?',
    'How do I send a message to a list of parents?',
    'Which access group do I need?',
]

/** True when `pathname` is `prefix` or a page under it. */
const under = (pathname, prefix) =>
    prefix.endsWith('/')
        ? pathname.startsWith(prefix)
        : pathname === prefix || pathname.startsWith(`${prefix}/`)

/**
 * The three starter questions for the page the panel is opened on.
 *
 * @param {string} pathname  the router path, e.g. "/pipelines/new"
 * @returns {string[]}
 */
export const starterQuestions = (pathname = '/') => {
    const found = STARTERS.find(([prefixes]) =>
        prefixes.some((prefix) => under(pathname, prefix))
    )
    return found ? found[1] : DEFAULT_STARTERS
}

// The service's lookup tools (services assistant_tools.py TOOLS) in words.
const LOOKED_UP = {
    find_template: 'Templates',
    pipelines_for_event: 'Pipelines',
    get_pipeline: 'Pipelines',
    count_pipelines: 'Pipelines',
    send_status: 'Send status',
    recent_comms_to: 'Comm logs',
    scheduled_sends: 'Scheduled sends',
    holidays: 'Holidays',
    sender_emails: 'Sender emails',
    template_categories: 'Template categories',
    ai_status: 'AI settings',
    my_access: 'Your access',
}

/**
 * What an answer looked up, in words and once each: "Templates, Pipelines".
 * A tool the page does not know yet is shown by its name with spaces.
 *
 * @param {string[]} names  the answer's `looked_up`
 * @returns {string} '' when nothing was looked up
 */
export const lookedUpLabel = (names) => {
    const words = (Array.isArray(names) ? names : [])
        .filter((name) => typeof name === 'string' && name)
        .map((name) => LOOKED_UP[name] ?? name.replace(/_/g, ' '))
    return [...new Set(words)].join(', ')
}

// The page's name for the question's context, longest path first.
const PAGE_TITLES = [
    ['/pipelines/new', 'New pipeline'],
    ['/pipelines/edit', 'Edit pipeline'],
    ['/pipeline/history', 'Deleted versions'],
    ['/pipelines', 'Pipelines'],
    ['/bulk-send', 'Bulk Send'],
    ['/reports', 'Reports'],
    ['/holidays/new', 'New holiday'],
    ['/holidays/bulk', 'Bulk holiday upload'],
    ['/holidays', 'Holidays'],
    ['/templates', 'Templates'],
    ['/create-template', 'Create Template'],
    ['/edit-template', 'Edit Template'],
    ['/upload-template', 'Upload Template'],
    ['/test-template', 'Test template'],
    ['/approve-template', 'Approve Template'],
    ['/ai/template-writer', 'Template Writer'],
    ['/ai/csv-validator', 'CSV Validator'],
    ['/ai/bulk-preview', 'Bulk Preview'],
    ['/ai/campaign-brief', 'Campaign Brief'],
    ['/ai/performance-insights', 'Performance Insights'],
    ['/ai/ab-variants', 'A/B Variants'],
    ['/ai/unsubscribe-risk', 'Unsubscribe Risk'],
    ['/ai/audience-overlap', 'Audience Overlap'],
    ['/ai/crm-enrich', 'CRM Enrich'],
    ['/ai/comms-finder', 'Comms Finder'],
    ['/reputation', 'Reputation'],
    ['/segments', 'Segments'],
    ['/settings', 'Settings'],
    ['/guide', 'Guide'],
].sort((a, b) => b[0].length - a[0].length)

/**
 * The name of the page at `pathname`, sent with a question so the answer
 * can start from where the person is ("Bulk Send"). The document title is
 * the same on every page, so it would say nothing.
 *
 * @param {string} pathname
 * @returns {string}
 */
export const pageTitleFor = (pathname = '/') => {
    if (pathname === '/') return 'Home'
    const found = PAGE_TITLES.find(([prefix]) => under(pathname, prefix))
    return found ? found[1] : 'Comms'
}

/**
 * A conversation's last activity in words: "just now", "5 min ago",
 * "3 h ago", "yesterday", "4 days ago", then the date.
 *
 * @param {number} epochSeconds
 * @param {number} [nowMs]  the clock (tests pass one)
 * @returns {string}
 */
export const relativeTime = (epochSeconds, nowMs = Date.now()) => {
    if (!Number.isFinite(Number(epochSeconds)) || !epochSeconds) return ''
    const seconds = Math.max(0, Math.floor(nowMs / 1000 - Number(epochSeconds)))
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ago`
    const days = Math.floor(hours / 24)
    if (days === 1) return 'yesterday'
    if (days < 7) return `${days} days ago`
    return new Date(Number(epochSeconds) * 1000).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    })
}

/**
 * The ids of the failed answers that offer Retry: for each question with
 * no answer yet, its latest failed one. A failure already answered by a
 * retry stays in the conversation without a button (the service refuses a
 * second answer to a question).
 *
 * @param {object[]} messages  the conversation, oldest first
 * @returns {Set<string>}
 */
export const retryableFailures = (messages) => {
    const answered = new Set()
    const latestFailure = new Map()
    ;(messages ?? []).forEach((m) => {
        if (m.sender !== 'bot' || !m.answers) return
        if (m.failed) latestFailure.set(m.answers, m.id)
        else answered.add(m.answers)
    })
    return new Set(
        [...latestFailure]
            .filter(([question]) => !answered.has(question))
            .map(([, id]) => id)
    )
}

/**
 * What this browser remembers of BhanzuBot: the open conversation and
 * whether the panel was open. Blocked storage (a private window) reads as
 * nothing remembered.
 *
 * @returns {{conversationId: string|null, open: boolean}}
 */
export const readBotStorage = () => {
    try {
        return {
            conversationId:
                window.localStorage.getItem(BOT_CONVERSATION_KEY) || null,
            open: window.localStorage.getItem(BOT_OPEN_KEY) === 'true',
        }
    } catch {
        return { conversationId: null, open: false }
    }
}

/**
 * Remember the conversation (null forgets it) and whether the panel is
 * open; blocked storage only forgets it.
 *
 * @param {{conversationId: string|null, open: boolean}} state
 */
export const writeBotStorage = ({ conversationId, open }) => {
    try {
        if (conversationId)
            window.localStorage.setItem(BOT_CONVERSATION_KEY, conversationId)
        else window.localStorage.removeItem(BOT_CONVERSATION_KEY)
        window.localStorage.setItem(BOT_OPEN_KEY, open ? 'true' : 'false')
    } catch {
        // Storage blocked: the state lasts until the page is reloaded.
    }
}
